import { animate, query, stagger, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, OnDestroy, Inject, inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	concatMap,
	debounce,
	debounceTime,
	filter,
	iif,
	interval,
	map,
	merge,
	of,
	startWith,
	Subject,
	take,
	takeUntil,
	tap,
	throttleTime,
	withLatestFrom,
} from 'rxjs';
import {
	CreatePalletBody,
	CreatePalletResponse,
	LikutDialogDataInput,
	LikutDialogPageModes,
	LikutPalette,
	LikutPaletteMoves,
	LikutPalletProducts,
	LikutPalletValidation,
	LikutPalletValidationWithTimestamp,
	TransferPalletBody,
} from 'src/app/Interfaces/likut';
import { LoadingStatuses } from 'src/app/Interfaces/util';
import { ButtonTextPipe } from 'src/app/Pipes/button-text.pipe';
import { AlertService } from 'src/app/services/alert.service';
import { AudioService } from 'src/app/services/audio.service';
import { BarcodeService } from 'src/app/services/barcode.service';
import { BottomPopupService } from 'src/app/services/bottom-popup.service';
import { LikutWarehouseService } from 'src/app/services/likut-warehouse.service';
import { LoadingService } from 'src/app/services/loading.service';
import { StoreService } from 'src/app/services/store.service';
import { MAX_INT32 } from 'src/app/Utility/constants';
import { translateDateForSQLServer } from 'src/app/Utility/date_functions/SQL_ISO_adapter';
import { v4 as uuid } from 'uuid';

@Component({
	selector: 'app-reprint-dialog',
	templateUrl: './likut-warehouse-dialog.component.html',
	styles: [
		`
			:host {
				display: grid;
				place-items: center;
				gap: 1rem;
				position: relative;
				padding: 10px 0;
			}

			button {
				font-size: large;
			}

			.pallet-wrapper {
				display: flex;
				flex-direction: column;
				gap: 1rem;
				overflow-y: auto;
				max-height: 60vh;
			}

			.pallet-row {
				width: 100%;
				display: flex;
				justify-content: space-between;
			}

			@media (prefers-reduced-motion: no-preference) {
				.pallet-row {
					opacity: 0.2;
					max-height: 1px;
					transition: all 0.3s;

					&.in {
						opacity: 1;
						max-height: 100%;
					}
				}
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LikutWarehouseDialogComponent implements OnInit, OnDestroy {
	_ss = inject(StoreService);
	_likutWarehouse = inject(LikutWarehouseService);
	_loading = inject(LoadingService);

	private fb = inject(FormBuilder);
	private _barcode = inject(BarcodeService);
	private _bottomPopup = inject(BottomPopupService);
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private _audio = inject(AudioService);
	private _alert = inject(AlertService);

	constructor(@Inject(MAT_DIALOG_DATA) private data: LikutDialogDataInput) {}

	title$ = new BehaviorSubject<string>(this.data.titleToDisplay || '');
	buttonText$ = new BehaviorSubject<string>(this.data.buttonText || 'שלח');

	dialogMode$ = new BehaviorSubject<LikutDialogPageModes>(this.data.mode);

	shouldButtonShow$ = this.dialogMode$.pipe(map(mode => mode !== 'pallet-info-choosePallet' && mode !== 'pallet-info-palletInfo'));
	forcedLocale = this._likutWarehouse.forcedButtonsAndTextTypeof$.value;

	// forms
	createPalletForm = this.fb.group({
		date: [new Date(), Validators.required],
		warehouse: [<string | null>'', Validators.required],
		palletNum: [1, [Validators.required, Validators.max(100_000)]],
		extra4: [<string>''],
		packsPerPallet: [<number | null>null, Validators.required],
		printer: [<number | null>null, Validators.required],
	});

	// palletInfoWithProduct$ = new BehaviorSubject<(LikutPalette & LikutPalletProducts) | null>(null);
	palletInfoForm = this.fb.group({
		tokefDate: [new Date()],
		createDate: [new Date()],
		extra4: [<string>''],
		packsPerPallet: [<number | null>null],
		user: [<string>''],
		warehouse: [<string | null>null],
	});

	chooseProductForm = this.fb.nonNullable.group({
		product: [<string>'', this._likutWarehouse.validateLikutPalletProduct.bind(this._likutWarehouse)],
	});
	chooseWarehouseForm = this.fb.group({
		warehouse: [<string | null>null, Validators.required],
	});
	transferOrSendoutPalletForm = this.fb.group({
		pallet: [<string | null>null, Validators.required],
	});

	reprintForm = this.fb.group({
		pallet: [<string | null>null, Validators.required],
		printer: [<number | null>null, Validators.required],
	});
	filteredProducts$ = this.chooseProductForm.controls.product.valueChanges.pipe(
		startWith(''),
		map(value => this._filter<LikutPalletProducts>(value || '', this._likutWarehouse.products$.value, ['ProductName', 'ProductId'])),
	);

	chosenProduct$ = new BehaviorSubject<LikutPalletProducts | null>(null);

	readonly dateRange = {
		min: new Date(),
		max: new Date(new Date().setFullYear(new Date().getFullYear() + 50)),
	};

	isReprintFormValid$ = this._isFormValid$(this.reprintForm);
	isCreateChooseProductFormValid$ = this._isFormValid$(this.chooseProductForm);
	isCreatePalletFormValid$ = this._isFormValid$(this.createPalletForm);
	isTransferChooseWarehouseFormValid$ = this._isFormValid$(this.chooseWarehouseForm);

	scannedPallets$ = new BehaviorSubject<LikutPalletValidationWithTimestamp[]>([]);
	scannedPalletsDisplay$ = this.scannedPallets$.pipe(map(pallets => pallets.sort((a, b) => b.timestamp - a.timestamp)));
	isTransferPalletFormValid$ = this.scannedPallets$.pipe(map(pallets => pallets.length > 0));

	isSubmitButtonDisabled$ = combineLatest([
		this.dialogMode$,
		this.isReprintFormValid$,
		this.isCreatePalletFormValid$,
		this.isCreateChooseProductFormValid$,
		this.isTransferChooseWarehouseFormValid$,
		this.isTransferPalletFormValid$,
	]).pipe(
		map(([mode, isReprintValid, isCreatePalletValid, isCreateChooseProductValid, isTransferChooseWarehouseFormValid, isTransferPalletFormValid]) => {
			switch (mode) {
				case 'reprint':
					return !isReprintValid;
				case 'create-chooseProduct':
				case 'create-w-chooseProduct':
					return !isCreateChooseProductValid;
				case 'create-createPallet':
				case 'create-w-createPallet':
					return !isCreatePalletValid;
				case 'transfer-chooseWarehouse':
				case 'transferFromAllInventoryReport':
				case 'inventory-scan':
					return !isTransferChooseWarehouseFormValid;

				case 'transfer-transferPallet':
				case 'delete-sendPalletOut':
					return !isTransferPalletFormValid;
				default:
					return false;
			}
		}),
	);

	destroy$ = new Subject<void>();

	loadingState$ = new BehaviorSubject<LoadingStatuses>('idle');

	successMessage$ = combineLatest([this.dialogMode$, this._likutWarehouse.buttonsAndText$]).pipe(
		map(([mode, buttonsAndText]) => {
			const modeToTextID = {
				reprint: 'pallet-reprint-success-message',
				'create-createPallet': 'create-pallet-success-message',
				'create-w-createPallet': 'create-pallet-success-message',
				'transfer-transferPallet': 'pallet-transfer-success-message',
				'delete-sendPalletOut': 'pallet-send-out-success-message',
				transferFromAllInventoryReport: 'pallet-transfer-success-message',
			};
			if (!modeToTextID[mode]) return '';
			return buttonsAndText[modeToTextID[mode]].main + (buttonsAndText[modeToTextID[mode]].sub ? ' - ' + buttonsAndText[modeToTextID[mode]].sub : '');
		}),
	);

	createdPalletIDs$ = new BehaviorSubject<number[]>([]);

	palletTransferFormSubmit$ = new BehaviorSubject<string>('');
	palletInfoFormSubmit$ = new BehaviorSubject<string>('');

	palletIDStream$ = merge(this._barcode.createBarcodeStream(), merge(this.palletTransferFormSubmit$, this.palletInfoFormSubmit$).pipe(filter(pallet => !!pallet)));

	imageUrl = this._ss.CustomerInformation.CustImageDomain;

	buttonTextPipe = ButtonTextPipe.prototype.transform;

	palletInfoPalletID$ = new BehaviorSubject<number | null>(null);

	// Some future customers might not want to add the main remark
	shouldAddMainRemark$ = new BehaviorSubject<boolean>(true);

	ngOnInit(): void {
		this.palletInfoForm.disable();

		this._barcode.updateCharacters();

		this.handleFilterCharactersFromAllInputs();

		this.handleDataPayload();

		// if (environment.development){
		// 	this.loadingState$.next('success')
		// }

		this._likutWarehouse.lastPrinterSelected$.pipe(take(1)).subscribe(printer => {
			this.reprintForm.controls.printer.setValue(printer?.PrinterID || null);
			this.createPalletForm.controls.printer.setValue(printer?.PrinterID || null);
		});

		this._likutWarehouse.lastWarehouseSelected$.pipe(take(1)).subscribe(warehouse => {
			// if (this.dialogMode$.value === 'create-w-createPallet') return;
			this.createPalletForm.controls.warehouse.setValue(warehouse?.WarehouseID || null);
		});

		this.reprintForm.controls.printer.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(printer => {
			if (printer) {
				this._likutWarehouse.printerInLocalStorage$.next(printer);
				localStorage.setItem('lastPrinterSelected', String(printer));
			}
		});

		this.createPalletForm.controls.warehouse.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(warehouse => {
			if (!warehouse) return;
			// if (!warehouse || this.dialogMode$.value === 'create-w-createPallet') return;
			this._likutWarehouse.warehouseInLocalStorage$.next(warehouse);
			localStorage.setItem('lastWarehouseSelected', warehouse);
		});

		this.createPalletForm.controls.printer.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(printer => {
			if (printer) {
				this._likutWarehouse.printerInLocalStorage$.next(printer);
				localStorage.setItem('lastPrinterSelected', String(printer));
			}
		});

		this.dialogMode$.pipe(takeUntil(this.destroy$)).subscribe(mode => {
			this._likutWarehouse.refreshObjectReferenceForButtonsTextTypeof();
			switch (mode) {
				case 'reprint':
					this._barcode
						.createBarcodeStream()
						.pipe(takeUntil(this.destroy$))
						.subscribe(barcode => {
							this.reprintForm.controls.pallet.setValue(barcode);
						});
					break;
				case 'create-chooseProduct':
				case 'create-w-chooseProduct':
					if (mode === 'create-w-chooseProduct') {
						this.createPalletForm.controls.warehouse.setValue('W');
						this.createPalletForm.controls.warehouse.disable();
						this.createPalletForm.controls.palletNum.setValue(1);
						this.createPalletForm.controls.palletNum.disable();
					}
					combineLatest([this._barcode.createBarcodeStream()])
						.pipe(takeUntil(this.destroy$))
						.subscribe(([barcode]) => {
							const buttonsAndText = {};
							this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(buttonsAndText => {
								buttonsAndText = buttonsAndText;
							});
							const product = this._likutWarehouse.products$.value.filter(p => p.Barcode === barcode);
							if (product.length === 1) {
								this.chosenProduct$.next(product[0]);
								this._audio.playAudio('likut-warehouse-scan-success');
								this.handleDialogSubmit();
								return;
							}

							if (product.length > 1) {
								const message = this.buttonTextPipe(buttonsAndText['snackbar-multiple-products-found']);
								this._bottomPopup.show(message);
								this.chooseProductForm.controls.product.setValue(product[0].ProductName);
								return;
							}

							const message = this.buttonTextPipe(buttonsAndText['snackbar-product-not-found']);
							this._audio.playAudio('likut-warehouse-scan-failure');
							this._bottomPopup.show(message);
						});
					this.chooseProductForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(formValues => {
						const { product } = formValues;
						const productObject = this._likutWarehouse.products$.value.find(p => p.ProductId === product);
						if (productObject) {
							this.chosenProduct$.next(productObject);
						}
					});

					break;
				case 'create-createPallet':
				case 'create-w-createPallet':
					// TODO handle barcode scanner
					this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(buttonsAndText => {
						this.title$.next(buttonsAndText['create-pallet-title'].main);
						this.buttonText$.next(buttonsAndText['create-pallet-next-stage-button'].main);
					});
					break;

				case 'transfer-transferPallet':
				case 'delete-sendPalletOut':
					this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(buttonsAndText => {
						if (this.dialogMode$.value === 'transfer-transferPallet') {
							this.title$.next(buttonsAndText['pallet-transfer-title-next-stage'].main);
							this.buttonText$.next(buttonsAndText['pallet-transfer-title-next-stage-button'].main);
						}
					});

					this.palletIDStream$
						.pipe(
							takeUntil(this.destroy$),
							withLatestFrom(this.scannedPallets$, this._likutWarehouse.buttonsAndText$),
							concatMap(([PaletteID, scannedPallets, buttonsAndText]) =>
								iif(
									// sql server column type is int, so we need to check if the value is within the range of int32
									() => +PaletteID <= MAX_INT32 && !scannedPallets.some(p => p.PaletteID === +PaletteID),
									this._likutWarehouse.checkIfPalletIDValid(PaletteID).pipe(
										map(pallet => ({ PaletteID, pallet, networkError: false, buttonsAndText })),
										catchError(error => {
											this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-network-error']));
											return of({ PaletteID, pallet: null, networkError: true, buttonsAndText });
										}),
									),
									of({ PaletteID, pallet: null, networkError: false, buttonsAndText }),
								),
							),
							tap(({ PaletteID, pallet, networkError, buttonsAndText }) => {
								if (!PaletteID || networkError) return;
								if (this.scannedPallets$.value.some(p => p.PaletteID === +PaletteID)) {
									this._audio.playAudio('likut-warehouse-scan-failure');
									this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-already-in-list']));
									return;
								}
								if (pallet && !this.scannedPallets$.value.some(p => p.PaletteID === +PaletteID)) {
									this._audio.playAudio('likut-warehouse-scan-success');
									this.scannedPallets$.next([...this.scannedPallets$.value, { ...pallet, timestamp: Date.now() }]);
									return;
								}
								if (!pallet) {
									this._audio.playAudio('likut-warehouse-scan-failure');
									this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-cant-be-added']));
								}
							}),
						)
						.subscribe();
					break;

				case 'pallet-info-choosePallet':
					this.palletIDStream$
						.pipe(
							takeUntil(this.destroy$),
							withLatestFrom(this._likutWarehouse.buttonsAndText$),
							concatMap(([PaletteID, buttonsAndText]) =>
								iif(
									// sql server column type is int, so we need to check if the value is within the range of int32
									() => +PaletteID <= MAX_INT32,
									this._likutWarehouse.getPalletInfoAfterScan(PaletteID).pipe(
										map(pallet => ({ PaletteID, pallet, networkError: false, buttonsAndText })),
										catchError(error => {
											this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-network-error']));
											return of({ PaletteID, pallet: null, networkError: true, buttonsAndText });
										}),
									),
									of({ PaletteID, pallet: null, networkError: false, buttonsAndText }),
								),
							),
							tap(
								({
									PaletteID,
									pallet,
									networkError,
									buttonsAndText,
								}: {
									PaletteID: string;
									pallet: (LikutPalletValidation & LikutPalette) | null;
									networkError: boolean;
									buttonsAndText: any;
								}) => {
									if (!PaletteID || networkError) {
										this._audio.playAudio('likut-warehouse-scan-failure');
										return;
									}
									if (this.scannedPallets$.value.some(p => p.PaletteID === +PaletteID)) {
										this._audio.playAudio('likut-warehouse-scan-failure');
										this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-already-in-list']));
										return;
									}
									if (pallet) {
										console.log(pallet);
										const product = this._likutWarehouse.products$.value.find(p => p.ProductId === pallet.ProductId);
										if (!product) {
											this._audio.playAudio('likut-warehouse-scan-failure');
											this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-cant-be-added']));
											return;
										}
										this.chosenProduct$.next(product);
										this.palletInfoPalletID$.next(pallet.PaletteID);
										this.palletInfoForm.setValue({
											tokefDate: pallet.TokefDate,
											createDate: pallet.CreateDate,
											extra4: pallet.Extra4,
											packsPerPallet: pallet.PackQTY,
											user: pallet.OrigUserID,
											warehouse: pallet.WarehouseID,
											// warehouse: 'A2',
										});
										this.createPalletForm.disable();
										this.dialogMode$.next('pallet-info-palletInfo');
										this._audio.playAudio('likut-warehouse-scan-success');

										return;
									}
									if (!pallet) {
										this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-cant-be-added']));
									}
								},
							),
						)
						.subscribe();
					break;
				case 'pallet-info-palletInfo':
					this.title$.next(this.title$.value + ' - ' + this.palletInfoPalletID$.value);
					break;

				default:
					break;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	sendReprintRequest() {
		if (this.loadingState$.value === 'loading') return;
		const { printer, pallet } = this.reprintForm.value;
		this.loadingState$.next('loading');
		this._likutWarehouse
			.sendReprintRequest(pallet, String(printer))
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.reprintForm.controls.pallet.reset();
					this.reprintForm.controls.pallet.markAsUntouched();

					this.loadingState$.next('success');
					this._audio.playAudio('likut-warehouse-action-success');

					setTimeout(() => {
						this.loadingState$.next('idle');
					}, 3000);
				},
				error: () => {
					this.loadingState$.next('error');
				},
			});
	}

	sendCreatePalletRequest() {
		if (this.createPalletForm.invalid || this.chosenProduct$.value === null) {
			this.loadingState$.next('error');
			return;
		}
		const { date, warehouse, palletNum, packsPerPallet, printer, extra4 } = this.createPalletForm.value;
		const { ItemPack, ProductId, Barcode, Extra1, Extra2, Extra3, Color } = this.chosenProduct$.value;
		const body: CreatePalletBody = [];
		let WarehouseID = warehouse;
		let palletNumber = palletNum;
		if (this.dialogMode$.value === 'create-w-createPallet') {
			WarehouseID = 'W';
			palletNumber = 1;
		}
		for (let i = 0; i < palletNumber; i++) {
			const _id = uuid();
			body.push({
				table: 'LikutPalette',
				ProductId: ProductId || '',
				Barcode: Barcode || '',
				PackQTY: packsPerPallet,
				ItemPack: ItemPack || 1,
				TokefDate: translateDateForSQLServer(date),
				Extra1: Extra1 || null,
				Extra2: Extra2 || null,
				Extra3: Extra3 || null,
				Extra4: extra4 || null,
				Status: null,
				Color: Color || null,
				Active: null,
				PrintStatus: 0,
				PrinterID: printer,
				_id,
			});

			body.push({
				table: 'LikutPaletteMoves',
				WarehouseID,
				Extra1: Extra1 || null,
				Extra2: Extra2 || null,
				Extra3: Extra3 || null,
				UserID: this._ss.AgentIdConnected || null,
				Color: Color || null,
				ProductId: ProductId || '',
				_id,
			});
		}
		this.loadingState$.next('loading');
		this._likutWarehouse
			.sendCreatePalletRequest(body)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: CreatePalletResponse) => {
					this.createPalletForm.markAsUntouched();

					if (res.err === true) {
						this.loadingState$.next('error');
						return;
					}

					this.createdPalletIDs$.next(res.palletIDs);

					this.loadingState$.next('success');
					this._audio.playAudio('likut-warehouse-action-success');

					setTimeout(() => {
						const product = this.chosenProduct$.value;
						this.loadingState$.next('idle');
						this.createPalletForm.setValue({
							date: new Date(new Date().setMonth(new Date().getMonth() + (+product.Tokef || 0))),
							warehouse: WarehouseID,
							palletNum: 1,
							extra4: '',
							packsPerPallet: product.PackPerPallet,
							printer,
						});
						setTimeout(() => {
							// update status of the form, to refresh button state
							this.createPalletForm.updateValueAndValidity();
						}, 0);
					}, 3000);
				},
				error: () => {
					this.loadingState$.next('error');
				},
			});
	}

	sendTransferPalletRequest() {
		this.loadingState$.next('loading');
		const pallets = this.scannedPallets$.value;
		let { warehouse } = this.chooseWarehouseForm.value;
		if (this.dialogMode$.value === 'delete-sendPalletOut') {
			warehouse = '99';
		}
		const body: TransferPalletBody[] = pallets.map(p => ({
			PaletteID: p.PaletteID,
			WarehouseID: '' + p.WarehouseID,
			Count: -1,
			UserID: this._ss.AgentIdConnected || null,
		}));
		const loopCount = body.length;
		for (let i = 0; i < loopCount; i++) {
			const pallet = pallets[i];
			body.push({
				PaletteID: pallet.PaletteID,
				WarehouseID: '' + warehouse,
				Count: 1,
				UserID: this._ss.AgentIdConnected || null,
			});
		}

		this._likutWarehouse.sendTransferPalletRequest(body).subscribe({
			next: () => {
				this.loadingState$.next('success');
				this._audio.playAudio('likut-warehouse-action-success');

				this.scannedPallets$.next([]);
				setTimeout(() => {
					if (this.dialogMode$.value === 'transferFromAllInventoryReport') {
						this.dialog.closeAll();
						return;
					}
					this.loadingState$.next('idle');
				}, 3000);
			},
			error: () => {
				this.loadingState$.next('error');
				setTimeout(() => {
					this.loadingState$.next('idle');
				}, 3000);
			},
		});
	}

	removeScannedPallet(input: { e: Event; pallet: LikutPalletValidation }) {
		const { e, pallet } = input;
		if (!('target' in e) || !(e.target instanceof HTMLElement)) return;
		e.target.parentElement.parentElement.classList.remove('in');
		e.target.parentElement.parentElement.parentElement.classList.remove('in');
		e.target.parentElement.parentElement.parentElement.parentElement.classList.remove('in');

		setTimeout(() => {
			this.scannedPallets$.next(this.scannedPallets$.value.filter(p => p.PaletteID !== pallet.PaletteID));
		}, 300);
	}

	handleDialogSubmit() {
		switch (this.dialogMode$.value) {
			case 'reprint':
				this.sendReprintRequest();
				return;
			case 'create-createPallet':
			case 'create-w-createPallet':
				this.sendCreatePalletRequest();
				return;
			case 'create-chooseProduct':
			case 'create-w-chooseProduct':
				this.chosenProduct$.pipe(take(1)).subscribe(product => {
					console.log(product);

					if (product) {
						this.createPalletForm.controls.packsPerPallet.setValue(product.PackPerPallet);
						const tokef = isFinite(+product.Tokef) ? +product.Tokef : 0;
						const date = new Date();
						const month = date.getMonth();
						date.setMonth(month + tokef);
						this.createPalletForm.controls.date.setValue(date);
					}
				});
				if (this.dialogMode$.value === 'create-chooseProduct') {
					this.dialogMode$.next('create-createPallet');
				} else {
					this.dialogMode$.next('create-w-createPallet');
				}

				return;
			case 'transfer-chooseWarehouse':
				this.dialogMode$.next('transfer-transferPallet');
				break;
			case 'delete-sendPalletOut':
			case 'transfer-transferPallet':
			case 'transferFromAllInventoryReport':
				this.sendTransferPalletRequest();
				break;
			case 'inventory-scan':
				this._likutWarehouse.inventoryScanSelectedWarehouse$.next(this.chooseWarehouseForm.value.warehouse);
				this.dialog.closeAll();
				this.router.navigate(['likut', 'warehouse', 'inventory-scan']);
				break;

			default:
				break;
		}
	}

	handleDataPayload() {
		const { startFromWarehouse, startWithPallet } = this.data;

		if (startFromWarehouse) {
			// switch (this.dialogMode$.value) {
			// 	case 'transfer-chooseWarehouse':
			// 	case 'transfer-transferPallet':
			// 		this.chooseWarehouseForm.controls.warehouse.setValue(startFromWarehouse);
			// 		break;
			// 	default:
			// 		break;
			// }
		}

		if (startWithPallet) {
			switch (this.dialogMode$.value) {
				case 'transferFromAllInventoryReport':
					this.scannedPallets$.next([{ ...startWithPallet, timestamp: Date.now() }]);

					break;
				default:
					break;
			}
		}
	}

	handleFormSetups() {
		if (this.shouldAddMainRemark$.value) {
			this.createPalletForm.controls.extra4.setValidators(Validators.required);
		}
	}

	handleFilterCharactersFromAllInputs() {
		// TODO handle both prefix and suffix, currently only prefix
		const char = this._barcode.barcodePrefixCharacter;
		const initiateSubscription = (control: AbstractControl) => {
			control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue: string | number) => {
				if (!newValue) return;

				newValue = typeof newValue === 'number' ? newValue : newValue.replaceAll(char, '');
				control.setValue(newValue, { emitEvent: false });
			});
		};
		const controls = [
			this.reprintForm.controls.pallet,
			this.reprintForm.controls.printer,
			this.createPalletForm.controls.palletNum,
			this.createPalletForm.controls.packsPerPallet,
			this.createPalletForm.controls.printer,
			this.createPalletForm.controls.warehouse,
			this.chooseProductForm.controls.product,
			this.chooseWarehouseForm.controls.warehouse,
			this.transferOrSendoutPalletForm.controls.pallet,
		];
		controls.forEach(initiateSubscription);
	}

	async onPressClose() {
		if (this.loadingState$.value === 'loading') return;
		switch (this.dialogMode$.value) {
			case 'create-chooseProduct':
			case 'create-w-chooseProduct':
			case 'pallet-info-choosePallet':
			case 'pallet-info-palletInfo':
			case 'inventory-scan':
				if (this.createPalletForm.pristine || this.createPalletForm.invalid) {
					this.dialog.closeAll();
					return;
				}
				break;
			case 'transfer-chooseWarehouse':
			case 'delete-sendPalletOut':
				if (this.scannedPallets$.value.length === 0) {
					this.dialog.closeAll();
					return;
				}
				break;
		}
		this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(async buttonsAndText => {
			const message = this.buttonTextPipe(buttonsAndText['dialog-close-confirmation']);
			const result = await this._alert.customConfirmAlert(message);
			if (result) {
				this.dialog.closeAll();
			}
		});
	}

	// convertLikutPalletMovesToLikutPalletValidation(moves: LikutPaletteMoves[]): LikutPalletValidation[] {
	// 	return moves.map(move => ({
	// 		PaletteID: move.PaletteID,
	// 		WarehouseID: move.WarehouseID,
	// 		CreateDate: move.MoveDate,
	// 		instock: move.Count,
	// 		ProductId: move.ProductId,
	// 		ProductName: move.ProductName,
	// 		TokefDate: move.TokefDate,
	// 	}));
	// }

	private _filter<T>(value: string | number, array: T[], keys: Array<keyof T extends string ? keyof T : never>): T[] {
		const filterValue = String(value).toLowerCase();

		return array.filter(item => {
			for (const key of keys) {
				if (String(item[key]).toLowerCase().includes(filterValue)) {
					return true;
				}
			}
			return false;
		});
	}

	private _isFormValid$(form: FormGroup) {
		return form.statusChanges.pipe(
			startWith(form.status),
			map(status => status === 'VALID'),
		);
	}
}
